import { RefObject, useEffect, useState } from 'react';
import Form, {
  type Conditional,
  type ConditionalAction,
  type DynamicFieldData,
  type FieldTypes,
  type FieldValues,
  type Calculation,
} from '@restore/dynamic-forms';
import { Box, Container } from '@mui/material';
import { useSettings } from 'src/hooks/use-settings';
import DocumentService from 'src/services/documentService';
import AuthContext from 'src/routes/AuthContext';
import { parseConditionals } from 'src/services/documentService/parseConditionals';
import CommonLoader from 'src/utils/common-loader';
import SucessModal from '../sucess-modal';
import {
  errorForLoaderDocumentation,
  successForLoaderDocumentation,
} from 'src/redux/actions/documentationAction';
import { useDispatch } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error';

interface DynamicFormProps {
  formId: string;
  uuid: string;
  autosaveTimestamp: Date;
  setAutosaveTimestamp: (timestamp: Date) => void;
  containerRef: RefObject<HTMLDivElement>
}

const DynamicForm: React.FC<DynamicFormProps> = (props) => {
  const { formId, uuid, setAutosaveTimestamp, autosaveTimestamp, containerRef } = props;

  const settings = useSettings();
  const [storedFormId, setStoredFormId] = useState<string | null>(null);
  const [formFields, setFormFields] = useState<DynamicFieldData<FieldTypes>[]>([]);
  const [conditionals, setConditionals] = useState<Conditional<ConditionalAction>[]>([]);
  const [calculations, setCalculations] = useState<Calculation[]>([]);
  const [formStatus, setFormStatus] = useState<FormStatus>('loading');
  const [dateFields, setDateFields] = useState<string[]>([]);
  const dispatch = useDispatch();

  const realm =
    window.location.hostname === 'localhost'
      ? localStorage.getItem('realm')
      : window.location.hostname.split('-')[0];

  useEffect(() => {
    if(formId && !storedFormId) {
      setStoredFormId(formId);
      datadogRum.addAction('submitCallback_STORE', {formId, storedFormId}); 
    }
  }, [formId, storedFormId])

  useEffect(() => {
    if (formStatus === 'loading') {
      if (storedFormId && realm) {
        DocumentService.getFields(realm, storedFormId, uuid)
          .then((response) => {
            const conditionals = parseConditionals(response.conditionals);
            const fields = DocumentService.parseFields(response.questions);
            const dateFields = DocumentService.getDateFields(fields);
            
            if(response.autosaveTime) {
              setAutosaveTimestamp(new Date(`${response.autosaveTime}+00:00`))
            }

            setDateFields(dateFields);
            setConditionals(conditionals);
            setFormFields(fields);
            setCalculations(response.calculations || []);
            setFormStatus('ready');
          })
          .catch(() => {
            setFormStatus('error');
          });
      }
    }
  }, [formStatus, storedFormId, uuid, realm, setAutosaveTimestamp]);

  let component;

  const submitCallback = (fields: FieldValues) => {
    if (storedFormId && realm) {
      datadogRum.addAction('submitCallback_STARTED', {storedFormId, realm}); 
      setFormStatus('submitting');
      const strippedFields = DocumentService.stripCheckboxFalseValues(fields);
      const sanitizedFields = DocumentService.parseJotformDates(strippedFields, dateFields);
      DocumentService.submitForm(realm, storedFormId, uuid, sanitizedFields)
        .then((success) => {
          if (success) {
            datadogRum.addAction('submitCallback_SUCCESS', {storedFormId, realm});
            setFormStatus('success');
          } else {
            datadogRum.addAction('submitCallback_FAILED', {storedFormId, realm});
            setFormStatus('error');
          }
        })
        .catch((error) => {
          datadogRum.addAction('submitCallback_FAILED', {storedFormId, realm, error});
          setFormStatus('error');
        });
    }else{
      datadogRum.addAction('submitCallback_FAILED', { storedFormId, realm});
    }
  };

  const autosaveCallback = (fields: FieldValues) => {
    if (storedFormId && realm) {
      datadogRum.addAction('autosaveCallback_STARTED', {storedFormId, realm});
      const strippedFields = DocumentService.stripCheckboxFalseValues(fields);
      const sanitizedFields = DocumentService.parseJotformDates(strippedFields, dateFields);
      DocumentService.autosave(realm, storedFormId, uuid, sanitizedFields);
      datadogRum.addAction('autosaveCallback_SUCCESS', {storedFormId, realm, uuid});
      setAutosaveTimestamp(new Date());
    }else{
      datadogRum.addAction('autosaveCallback_FAILED', {storedFormId, realm});
    }
  };

  const handleSuccess = () => {
    dispatch(successForLoaderDocumentation('Success'));
  };

  const handleError = () => {
    dispatch(errorForLoaderDocumentation('Error'));
  };

  switch (formStatus) {
    case 'loading':
      component = <CommonLoader />;
      break;

    case 'ready':
      component = (
        <Form
          formFields={formFields}
          conditionals={conditionals}
          calculations={calculations}
          autosaveCallback={autosaveCallback}
          submitCallback={submitCallback}
          autosaveThrottle={5}
          lastSavedDate={autosaveTimestamp}
          containerRef={containerRef}
        />
      );
      break;

    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your submission has been received"
          close={handleSuccess}
        />
      );
      break;

    default:
      component = (
        <SucessModal
          title="Error"
          message="Something went wrong"
          isSucess={false}
          close={handleError}
        />
      );

      break;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth={settings.stretch ? false : 'xl'}>{component}</Container>
    </Box>
  );
};

export default DynamicForm;
